import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { routerTransition } from '../../shared/animations/router.animations';
import { of, Observable, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountAppBarExpandedComponentData } from '../account-app-bar-expanded/account-app-bar-expanded.component';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { readWrite, readOnly } from '../../user/user-permission/user-permission.data';
import { MatDialog } from '@angular/material/dialog';
import { AccountService } from '../account.service';
import { BusinessAdvisorPickerComponent } from '../../shared/business-advisor/business-advisor-picker/business-advisor-picker.component';
import { EntityService } from '../../entity/entity.service';
import { AdvisorData, AdvisorFacade, EntityData, HorizontalListData, MessageService, ProductCode } from 'common';
import { AccountData, AccountWithRenewalData } from '../account.model';
import { AccountProfileAdapter } from './account-profile.data';
import { DocuSignEnvelopeService } from '../../shared/signing-widget/docusign-envelope.service';
import { ApplicationEnvelopeData } from '../../shared/signing-widget/signing-widget.model';

@UntilDestroy()
@Component({
    selector: 'ifb-account-profile',
    templateUrl: './account-profile.component.html',
    styleUrls: ['./account-profile.component.scss'],
    providers: [AccountProfileAdapter],
    animations: routerTransition,
    standalone: false
})

export class AccountProfileComponent implements OnInit {
  advisorData: AdvisorData;
  data: AccountAppBarExpandedComponentData;
  canEditAccountInfo: boolean;
  salesforceViewMode: boolean;
  canEditAccountStatus: boolean;
  canReadAccountStatus: boolean;
  canEditAccountOtherInfo: boolean;
  canViewAdvisor: boolean;
  canEditAdvisor: boolean;
  canViewDocumentGeneration: boolean;
  canEditDocumentGeneration: boolean;
  entity: EntityData;
  otherInfoListItems: HorizontalListData[];
  envelope: ApplicationEnvelopeData;

  private canViewEnvelope: boolean;
  private envelopeSubscription: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly accountService: AccountService,
    private readonly userPermissionService: UserPermissionService,
    private readonly dialog: MatDialog,
    private readonly entityService: EntityService,
    private readonly messageService: MessageService,
    private readonly adapter: AccountProfileAdapter,
    private readonly router: Router,
    private readonly advisorFacade: AdvisorFacade,
    private readonly envelopeService: DocuSignEnvelopeService,
  ) { }
  
  readonly ProductCode = ProductCode;

  ngOnInit(): void {
    this.route.params.pipe(untilDestroyed(this))
      .subscribe(params => this.salesforceViewMode = params.uniqueId !== undefined);

    this.route.data.pipe(untilDestroyed(this))
      .subscribe(this.initData.bind(this));

    this.userPermissionService.granted([readWrite('servicing-accounts')])
      .subscribe(res => this.canEditAccountInfo = res);

    this.userPermissionService.granted([readOnly('servicing-account-status')])
      .subscribe(res => this.canReadAccountStatus = res);

    this.userPermissionService.granted([readWrite('servicing-account-status')])
      .subscribe(res => this.canEditAccountStatus = res);

    this.userPermissionService.granted([readOnly('servicing-advisors-assignment')])
      .subscribe(res => this.canViewAdvisor = res);

    this.userPermissionService.granted([readWrite('servicing-advisors-assignment')])
      .subscribe(res => this.canEditAdvisor = res);

    this.userPermissionService.granted([readWrite('servicing-account-other-info')])
      .subscribe(res => this.canEditAccountOtherInfo = res);

    this.userPermissionService.granted([readOnly('servicing-account-documents-generation')])
      .subscribe(res => this.canViewDocumentGeneration = res);

    this.userPermissionService.granted([readWrite('servicing-account-documents-generation')])
      .subscribe(res => this.canEditDocumentGeneration = res);

    this.userPermissionService.granted([readOnly('servicing-docusign-envelopes')])
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.canViewEnvelope = res;
        this.refreshEnvelope();
      });
  }

  private initData(data: AccountAppBarExpandedComponentData): void {
    this.data = {
      account: data.account,
      accountInfo: data.accountInfo,
      userCurrent: data.userCurrent,
      owners: data.owners,
      liquidityEvent: data.liquidityEvent,
      accountStatements: {
        alligatorMatch: data.account.alligatorMatch,
        loanId: data.account.id,
        ...data.account.statementGeneration
      }
    };

    this.otherInfoListItems = this.adapter.adaptListData(this.data?.account);
    this.refreshEnvelope();

    this.entityService.get(this.data.account.entityId)
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.entity = res;
      });

    this.refreshAdvisorData(data?.account?.advisorId);
  }

  private refreshAdvisorData(advisorId?: number): void {
    if (!advisorId) {
      this.advisorData = null;
      return;
    }

    this.advisorFacade.getAdvisor$(advisorId)
      .pipe(take(1))
      .subscribe((advisorData: AdvisorData) => this.advisorData = advisorData);
  }

  // A loan signed in-house has no envelope; the widget stays hidden for it.
  private refreshEnvelope(): void {
    // Cleared first and the previous request dropped, so a late answer for another loan never lands here.
    this.envelopeSubscription?.unsubscribe();
    this.envelope = null;

    if (!this.canViewEnvelope || !this.data?.account?.id)
      return;

    this.envelopeSubscription = this.envelopeService.getByLoan(this.data.account.id)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.messageService.error(error);
          return of<ApplicationEnvelopeData>(null);
        }),
        untilDestroyed(this))
      .subscribe((envelope: ApplicationEnvelopeData) => this.envelope = envelope);
  }

  changeAdvisor(): void {
    BusinessAdvisorPickerComponent.show(this.dialog)
      .pipe(switchMap((data) => {
        if (!data || !data.id) {
          return of();
        }
        return this.accountService.updateLoanAdvisor(this.data.account.id, data.id)
          .pipe(switchMap(() => this.refreshAccountData$()))
      }))
      .subscribe((data: AccountData) => {
        if (data) {
          this.messageService.success(`Advisor successfully changed to ${data.advisorName}`);
        }
      })
  }

  private refreshAccountData$(): Observable<AccountData> {
    return this.accountService.get(this.data.account.id).pipe(tap((data: AccountData) => {
      this.data.account = data;
      this.accountService.onCurrentLoanChanged.next(data);
      this.refreshAdvisorData(data?.advisorId);
    }));
  }

  refreshAccountData(): void {
    this.refreshAccountData$().subscribe();
  }

  onEditOtherInfo(): void {
    this.router.navigate(['other-info'], { relativeTo: this.route });
  }
}
